// A tab left open across a deploy requests a lazy chunk whose hashed file no
// longer exists. After T02 that request 404s instead of returning index.html,
// and Vite fires `vite:preloadError`. One guarded reload fetches the new
// index.html and its current chunk names.
//
// The guard is a sessionStorage timestamp. It fails closed: if storage is
// blocked or the write cannot be read back, we never reload, so a persistent
// failure cannot turn into a reload loop.
import { storageGet, storageSet } from '@/utils/safe-storage';

export const RELOAD_KEY = 'chunk-reload-at';
export const RELOAD_WINDOW_MS = 10_000;

interface PreloadRecoveryDeps {
  now: () => number;
  reload: () => void;
}

export function handlePreloadError(deps: PreloadRecoveryDeps): void {
  const stored = Number(storageGet('session', RELOAD_KEY));
  const gap = deps.now() - stored;
  // A negative gap (stamp in the future, clock moved backwards) counts as stale.
  if (Number.isFinite(stored) && gap >= 0 && gap < RELOAD_WINDOW_MS) return;

  const stamp = String(deps.now());
  const written = storageSet('session', RELOAD_KEY, stamp);
  if (written && storageGet('session', RELOAD_KEY) === stamp) deps.reload();
}

// Deliberately never calls preventDefault: the original dynamic import still
// rejects so callers (e.g. src/utils/blog.ts) keep their own error handling.
export function installPreloadRecovery(win: Window = window): void {
  win.addEventListener('vite:preloadError', () => {
    handlePreloadError({ now: Date.now, reload: () => win.location.reload() });
  });
}
